import { Popover, IconButton } from "@radix-ui/themes";
import { useCallback, useEffect, useState, createContext } from "react";
import { useSurveyStore } from "@store/survey";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import PropTypes from "prop-types";
import { useLingui } from "@lingui/react";

MapPopover.propTypes = {
  children: PropTypes.node.isRequired,
  marker: PropTypes.object.isRequired,
  rootRef: PropTypes.object,
};

export const MapPopoverContext = createContext();

export default function MapPopover({ children, marker, rootRef }) {
  const [isVisible, setVisible] = useState(true);
  const mapRef = useSurveyStore((state) => state.getMapRef());
  const commentQuestion = useSurveyStore.getState().commentQuestion;
  const { i18n } = useLingui();

  useEffect(() => {
    if (!rootRef) return;

    if (commentQuestion) {
      setTimeout(() => {
        rootRef.unmount();
        marker.remove();
      }, 0);
    }

    return () => {
      setTimeout(() => {
        rootRef.unmount();
        marker.remove();
      }, 0);
    };
  }, [commentQuestion, marker, rootRef, i18n.locale]);

  const removeMarker = useCallback(() => {
    setVisible(false);
    setTimeout(() => marker.remove(), 50);
  }, [marker]);

  useEffect(() => {
    if (mapRef) {
      mapRef.on("click", removeMarker);
    }
    return () => {
      if (mapRef) {
        mapRef.off("click", removeMarker);
      }
    };
  }, [mapRef, removeMarker]);

  const escFunction = useCallback(
    (event) => {
      if (event.key === "Escape") {
        setVisible(false);
        setTimeout(() => marker.remove(), 50);
      }
    },
    [marker],
  );

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  if (!mapRef) return null;

  return (
    <Popover.Root open={isVisible}>
      <MapPopoverContext.Provider value={{ isVisible, setVisible }}>
        <Popover.Trigger>
          <IconButton
            onClick={removeMarker}
            variant="solid"
            color="gray"
            size="3"
          >
            <InfoCircledIcon width="15" height="15" />
          </IconButton>
        </Popover.Trigger>
        <Popover.Content size="2" width="300px" minHeight="100px">
          {children}
        </Popover.Content>
      </MapPopoverContext.Provider>
    </Popover.Root>
  );
}
